/**
 * The capabilities the composer borrows from surfaces it may not own.
 *
 * `03-ENGINEERING-SEAMS.md` §4 gives the media picker, the viewer and the voice
 * recap sheet to P37, and the prompt picker to the Journal destination. The
 * composer *hosts* all three — F-JR-30's Add menu and Voice button are its
 * toolbar — but it owns none of them, so they arrive as capabilities a host
 * supplies, defaulting to "not composed here".
 *
 * That default is truthful rather than a stub. §8.6 says an unavailable control
 * is an inert label reading "{title}, unavailable" and never a disabled button,
 * so an absent capability makes its control inert and says so out loud instead
 * of rendering a button that would do nothing. The alternative — hiding the
 * control — would violate §8.3's "no control may be removed", which is the rule
 * that keeps the accessibility collapse honest.
 */

import type { ReactNode } from "react";
import { createContext, useContext } from "react";
import type { JournalAttachment, JournalEntry, JournalPromptReference } from "@ff/domain";

export interface JournalComposerHost {
  /** F-JR-30: "Photo or video". Resolves once the picker has been dismissed. */
  readonly pickMedia?: (entry: JournalEntry) => void;
  /** F-JR-30: the Voice button. */
  readonly recordVoiceRecap?: (entry: JournalEntry) => void;
  /** F-JR-30: "Writing prompt". Returns the chosen prompt, or null on cancel. */
  readonly chooseWritingPrompt?: (entry: JournalEntry) => void;
  /** F-JR-30's attachment token strip: opening one in the media viewer. */
  readonly openAttachment?: (attachment: JournalAttachment) => void;
  /**
   * F-JR-19: what happens when a finish or a close actually dismisses. Supplied
   * by the session rather than by the composer, because the composer must not
   * be able to dismiss itself on an outcome that kept it open.
   */
  readonly onDismiss?: () => void;
  /** Applied after the prompt picker resolves; the composer owns the mutation. */
  readonly onPromptChosen?: (prompt: JournalPromptReference) => void;
}

const EMPTY_HOST: JournalComposerHost = Object.freeze({});

const JournalComposerHostContext = createContext<JournalComposerHost>(EMPTY_HOST);

export interface JournalComposerHostProviderProps {
  readonly host: JournalComposerHost;
  readonly children?: ReactNode;
}

export function JournalComposerHostProvider({
  host,
  children,
}: JournalComposerHostProviderProps): ReactNode {
  return (
    <JournalComposerHostContext.Provider value={host}>
      {children}
    </JournalComposerHostContext.Provider>
  );
}

/**
 * Defaults to the empty host rather than throwing, unlike `usePorts()`. A
 * missing port is a composition bug; a missing *host capability* is the ordinary
 * state of this build, and the composer has to render honestly without it.
 */
export function useJournalComposerHost(): JournalComposerHost {
  return useContext(JournalComposerHostContext);
}
